import axios, { AxiosInstance } from 'axios';
import { User, Project, Goal, Metric, MetricValue, AIDecomposition, CreateMetricRequest, UpdateMetricRequest, RecordMetricValueRequest, MetricForecast, GoalDependency, DependencyGraphNode, DependencyGraphEdge, ProjectSynergy, ImpactSimulation, DependencyAnalysis, ProjectArtifact, ProjectPlanningStep } from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('projectai_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('projectai_token');
          localStorage.removeItem('projectai_user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(email: string, password: string, name: string): Promise<{ user: User; token: string }> {
    const response = await this.api.post('/auth/register', { email, password, name });
    return response.data;
  }

  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async verifyToken(): Promise<{ user: User }> {
    const response = await this.api.post('/auth/verify');
    return response.data;
  }

  // Projects endpoints
  async getProjects(params?: { 
    status?: string; 
    limit?: number; 
    offset?: number; 
  }): Promise<{ projects: Project[]; pagination: any }> {
    const response = await this.api.get('/projects', { params });
    return response.data;
  }

  async getProject(id: string): Promise<{ project: Project }> {
    const response = await this.api.get(`/projects/${id}`);
    return response.data;
  }

  async createProject(projectData: {
    name: string;
    description: string;
    projectType: string;
    techStack?: string[];
    architecture?: string;
    existingAssets?: string[];
    totalBudget?: number;
    targetEndDate?: string;
  }): Promise<{ project: Project }> {
    const response = await this.api.post('/projects', projectData);
    return response.data;
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<{ project: Project }> {
    const response = await this.api.put(`/projects/${id}`, updates);
    return response.data;
  }

  async deleteProject(id: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/projects/${id}`);
    return response.data;
  }

  // Goals endpoints
  async getGoals(projectId: string, params?: {
    includeHierarchy?: boolean;
    includeMetrics?: boolean;
  }): Promise<{ goals: Goal[] }> {
    const response = await this.api.get('/goals', { 
      params: { projectId, ...params } 
    });
    return response.data;
  }

  async createGoal(goalData: {
    projectId: string;
    parentGoalId?: string;
    name: string;
    description?: string;
    estimatedHours?: number;
    targetDate?: string;
    complexityScore?: number;
    standalonePotential?: boolean;
  }): Promise<{ goal: Goal }> {
    const response = await this.api.post('/goals', goalData);
    return response.data;
  }

  async updateGoal(id: string, updates: {
    name?: string;
    description?: string;
    estimatedHours?: number;
    targetDate?: string;
    status?: string;
    progress?: number;
    complexityScore?: number;
    standalonePotential?: boolean;
  }): Promise<{ goal: Goal }> {
    const response = await this.api.put(`/goals/${id}`, updates);
    return response.data;
  }

  async deleteGoal(id: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/goals/${id}`);
    return response.data;
  }

  async extractGoal(id: string, data: {
    newProjectName: string;
    keepDependency?: boolean;
  }): Promise<{ project: Project }> {
    const response = await this.api.post(`/goals/${id}/extract`, data);
    return response.data;
  }

  // Metrics endpoints
  async getMetrics(goalId: string): Promise<{ metrics: Metric[] }> {
    const response = await this.api.get('/metrics', { params: { goalId } });
    return response.data;
  }

  async createMetric(metricData: CreateMetricRequest): Promise<{ metric: Metric }> {
    const response = await this.api.post('/metrics', metricData);
    return response.data;
  }

  async updateMetric(id: string, updates: UpdateMetricRequest): Promise<{ metric: Metric }> {
    const response = await this.api.put(`/metrics/${id}`, updates);
    return response.data;
  }

  async deleteMetric(id: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/metrics/${id}`);
    return response.data;
  }

  async getMetricValues(metricId: string, params?: {
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{ values: MetricValue[] }> {
    const response = await this.api.get(`/metrics/${metricId}/values`, { params });
    return response.data;
  }

  async recordMetricValue(metricId: string, data: RecordMetricValueRequest): Promise<{ metricValue: MetricValue }> {
    const response = await this.api.post(`/metrics/${metricId}/values`, data);
    return response.data;
  }

  async getMetricForecast(metricId: string): Promise<{ forecast: MetricForecast }> {
    const response = await this.api.get(`/metrics/${metricId}/forecast`);
    return response.data;
  }

  // AI endpoints
  async decomposeProject(projectId: string, data: {
    description: string;
    projectType: string;
    techStack?: string[];
    architecture?: string;
    existingAssets?: string[];
    budget?: number;
    timeline?: string;
    businessModel?: string;
    targetAudience?: string;
  }): Promise<{ decomposition: AIDecomposition }> {
    const response = await this.api.post('/ai/decompose', { projectId, ...data });
    return response.data;
  }

  async generateGoal(projectId: string, data: {
    description: string;
    context: any;
    parentGoal?: Goal;
    projectType?: string;
    systemPrompt?: string;
  }): Promise<AIDecomposition> {
    const response = await this.api.post(`/ai/generate-goal/${projectId}`, data);
    return response.data;
  }

  async acceptDecomposition(decompositionId: string): Promise<{ message: string }> {
    const response = await this.api.post(`/ai/decompositions/${decompositionId}/accept`);
    return response.data;
  }

  async submitDecompositionFeedback(decompositionId: string, data: {
    rating: number;
    feedback?: string;
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/ai/decompositions/${decompositionId}/feedback`, data);
    return response.data;
  }

  // Dependencies endpoints (US-007-009)
  async getDependencies(params?: {
    goalId?: string;
    projectId?: string;
  }): Promise<{ dependencies: GoalDependency[] }> {
    const response = await this.api.get('/dependencies', { params });
    return response.data;
  }

  async getDependenciesForGoal(goalId: string): Promise<{ dependencies: GoalDependency[] }> {
    const response = await this.api.get(`/dependencies/goal/${goalId}`);
    return response.data;
  }

  async createDependency(data: {
    sourceGoalId: string;
    targetGoalId: string;
    dependencyType: 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates';
    strength?: number;
    description?: string;
    impactPercentage?: number;
  }): Promise<{ dependency: GoalDependency }> {
    const response = await this.api.post('/dependencies', data);
    return response.data;
  }

  async updateDependency(id: string, updates: {
    dependencyType?: 'blocks' | 'enables' | 'reduces_complexity' | 'accelerates';
    strength?: number;
    description?: string;
    impactPercentage?: number;
  }): Promise<{ dependency: GoalDependency }> {
    const response = await this.api.put(`/dependencies/${id}`, updates);
    return response.data;
  }

  async deleteDependency(id: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/dependencies/${id}`);
    return response.data;
  }

  async getDependencyGraph(): Promise<{ nodes: DependencyGraphNode[]; edges: DependencyGraphEdge[] }> {
    const response = await this.api.get('/dependencies/graph');
    return response.data;
  }

  async getProjectSynergies(): Promise<{ synergies: ProjectSynergy[] }> {
    const response = await this.api.get('/dependencies/synergies');
    return response.data;
  }

  async simulateImpact(goalId: string, delayDays: number): Promise<ImpactSimulation> {
    const response = await this.api.post('/dependencies/impact-simulation', { goalId, delayDays });
    return response.data;
  }

  async getDependencyAnalysis(projectId: string): Promise<DependencyAnalysis> {
    const response = await this.api.get('/dependencies/analysis', { params: { projectId } });
    return response.data;
  }

  // Project Artifacts API methods
  async getProjectArtifacts(projectId: string): Promise<{ artifacts: ProjectArtifact[] }> {
    const response = await this.api.get('/artifacts', { params: { projectId } });
    return response.data;
  }

  async createProjectArtifact(artifactData: {
    projectId: string;
    artifactType: string;
    name: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }): Promise<{ message: string; artifact: ProjectArtifact }> {
    const response = await this.api.post('/artifacts', artifactData);
    return response.data;
  }

  async updateProjectArtifact(artifactId: string, updateData: {
    artifactType?: string;
    name?: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }): Promise<{ message: string; artifact: ProjectArtifact }> {
    const response = await this.api.put(`/artifacts/${artifactId}`, updateData);
    return response.data;
  }

  async deleteProjectArtifact(artifactId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/artifacts/${artifactId}`);
    return response.data;
  }

  // Planning Steps API methods
  async getPlanningSteps(projectId: string): Promise<{ planningSteps: ProjectPlanningStep[] }> {
    const response = await this.api.get('/planning', { params: { projectId } });
    return response.data;
  }

  async createPlanningStep(stepData: {
    projectId: string;
    stepName: string;
    stepOrder: number;
    description?: string;
    isRequired?: boolean;
    stepData?: any;
    notes?: string;
  }): Promise<{ message: string; planningStep: ProjectPlanningStep }> {
    const response = await this.api.post('/planning', stepData);
    return response.data;
  }

  async updatePlanningStep(stepId: string, updateData: {
    stepName?: string;
    stepOrder?: number;
    description?: string;
    isRequired?: boolean;
    isCompleted?: boolean;
    stepData?: any;
    notes?: string;
  }): Promise<{ message: string; planningStep: ProjectPlanningStep }> {
    const response = await this.api.put(`/planning/${stepId}`, updateData);
    return response.data;
  }

  async togglePlanningStepCompletion(stepId: string): Promise<{ message: string; planningStep: ProjectPlanningStep }> {
    const response = await this.api.post(`/planning/${stepId}/complete`);
    return response.data;
  }

  async deletePlanningStep(stepId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/planning/${stepId}`);
    return response.data;
  }

  // Project Type System API methods
  async post(url: string, data?: any) {
    const response = await this.api.post(url, data);
    return response;
  }

  async put(url: string, data?: any) {
    const response = await this.api.put(url, data);
    return response;
  }

  async get(url: string, params?: any) {
    const response = await this.api.get(url, params);
    return response;
  }
}

export const apiService = new ApiService();
export default apiService;